/* ============================================================================
 * Gestão de Documentos Fiscais — NFC-e
 *
 * Portado de base-html `erpone_gest_o_de_documentos_fiscais_nfc_e`:
 *   Status | Número/Série | Horário | Terminal PDV | Valor Total.
 *
 * Contrato real consumido (fiscal-documents.dto.ts):
 *   GET /fiscal/invoices?type=NFCE&status=&accessKey=&documentNumber=&page=&limit=
 *     → PaginatedFiscalDocumentListDto (data[] FiscalDocumentListItemDto)
 *   GET /fiscal/invoices/:id → FiscalDocumentDetailDto (modal de detalhe)
 *
 * O protótipo mostra painéis "Sincronizados (SEFAZ)" / "Contingência Offline" /
 * "Volume de Emissão (Hoje)" com totais agregados que a API NÃO publica
 * (não há `counts` nesta resposta). Para não inventar números, esses KPIs foram
 * omitidos — a contagem real (`total`) aparece na barra de filtros.
 * ========================================================================== */

import { useState } from 'react';
import { useAuth } from '../../../shared/auth/AuthContext';
import { formatCurrency, formatDateTime, EMPTY } from '../../../shared/utils/format';
import { Badge, Button, LoadingSkeleton } from '@nexora/design-system';
import { useFiscalDocuments } from './fiscalDocumentsHooks';
import {
  documentStatusLabel,
  documentStatusVariant,
  documentStatusIcon,
} from './fiscalStatusMaps';
import { FISCAL_DOCUMENTS_CSS } from './fiscalDocumentsCss';
import { FiscalDocumentDetailModal } from './FiscalDocumentDetailModal';

const STATUS_FILTERS = ['ALL', 'AUTHORIZED', 'PROCESSING', 'REJECTED', 'DENIED', 'CANCELLED', 'CONTINGENCY_PENDING', 'DRAFT', 'QUEUED'];

const PAGE_SIZE = 20;

export function NfceDocumentsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useFiscalDocuments(tenantId, {
    type: 'NFCE',
    status: statusFilter,
    accessKey: search || undefined,
    page: page + 1,
    limit: PAGE_SIZE,
  });

  const documents = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fiscal-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Documentos Fiscais (NFC-e)
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Emissões de varejo e seu status na SEFAZ.
          </p>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="fiscal-toolbar">
        <input
          className="fiscal-control"
          placeholder="Buscar por chave de acesso ou número..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          aria-label="Buscar por chave de acesso ou número"
        />
        <select
          className="fiscal-control fiscal-control--select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          aria-label="Filtrar por status"
        >
          <option value="ALL">Todos os status</option>
          {STATUS_FILTERS.filter((s) => s !== 'ALL').map((s) => (
            <option key={s} value={s}>
              {documentStatusLabel(s)}
            </option>
          ))}
        </select>
        <span className="fiscal-count">
          {total} {total === 1 ? 'documento' : 'documentos'}
        </span>
      </div>

      {/* ── Tabela ── */}
      <div className="fiscal-card">
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="40px" />
          </div>
        )}

        {error && !isLoading && (
          <div className="fiscal-state">
            <p className="fiscal-state__title">Erro ao carregar NFC-e</p>
            <p className="fiscal-state__detail">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && documents.length === 0 && (
          <div className="fiscal-state">
            <p className="fiscal-state__detail">
              Nenhuma NFC-e encontrada{statusFilter !== 'ALL' ? ' com o filtro atual' : ''}.
            </p>
          </div>
        )}

        {!isLoading && !error && documents.length > 0 && (
          <>
            <div className="fiscal-table">
              <table>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Número / Série</th>
                    <th>Horário</th>
                    <th>Terminal PDV</th>
                    <th style={{ textAlign: 'right' }}>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id} onClick={() => setSelectedId(doc.id)}>
                      <td>
                        <Badge variant={documentStatusVariant(doc.status)} size="sm">
                          <span
                            className="material-symbols-outlined fiscal-status-icon"
                            aria-hidden="true"
                          >
                            {documentStatusIcon(doc.status)}
                          </span>
                          {documentStatusLabel(doc.status)}
                        </Badge>
                      </td>
                      <td className="fiscal-mono">
                        {doc.documentNumber
                          ? `${String(doc.documentNumber).padStart(6, '0')} / ${doc.serie ?? 1}`
                          : EMPTY}
                      </td>
                      <td className="fiscal-nowrap">{formatDateTime(doc.issuedAt)}</td>
                      <td>
                        {doc.terminalPdv ? (
                          <span className="fiscal-recipient">{doc.terminalPdv}</span>
                        ) : (
                          EMPTY
                        )}
                      </td>
                      <td className="fiscal-num">{formatCurrency(doc.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="fiscal-footer">
              <span className="font-body-sm text-body-sm text-on-surface-variant tabular-nums">
                {page * PAGE_SIZE + 1}–{Math.min(total, page * PAGE_SIZE + documents.length)} de{' '}
                {total}
              </span>
              <div className="flex gap-xs">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <FiscalDocumentDetailModal
        documentId={selectedId}
        onClose={() => setSelectedId(null)}
      />

      <style>{FISCAL_DOCUMENTS_CSS}</style>
    </div>
  );
}